import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import _ from "lodash";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import { ko } from "date-fns/locale";

import VFlex from "../../layouts/VFlex";
import HFlex from "../../layouts/HFlex";
import Image from "../../layouts/Image";
import Text from "../../layouts/Text";
import Divider from "../../layouts/Divider";
import KakaoMapContainer from "../../components/KakaoMapContainer";
import VisitConfirmBottomSheet from "../VisitConfirmBottomSheet";
import SvgIcon, { SvgIconType } from "../../svg";
import TopBanner, { TopBannerLocation } from "../../components/TopBanner";

import "../../css/View.scss";

import { apiV1 } from "../../common/api";
import { RequestFetch, RESERVATION_STATUS } from "../../model/Request/Request";
import { useAppLinkContext } from "../../modules/useAppLink";
import useAmplitudeTrack, {
  amplitudeEvents,
} from "../../common/hooks/useAmplitudeTrack";
import { useConfig } from "../../context/Config";

export default function Reservation() {
  const { state } = useLocation();

  const { isWebView } = useConfig();

  const { trackEvent, setIdentifyObj } = useAmplitudeTrack();
  const navigate = useNavigate();
  const { initProperties, openAppLink } = useAppLinkContext();
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key") || searchParams.get("revNo");
  const { changeReservationStatus } = RequestFetch();
  const [warningMessage, setWarningMessage] = useState<string>();
  const [visitConfirmBottomSheetOpened, setVisitConfirmBottomSheetOpened] =
    useState(false);
  const [stageVisible, setStageVisible] = useState<boolean>(state);

  const [content, setContent] = useState<{
    status: number;
    cancelMemo: string;
    displayId: string;
    pet: any;
    group: any;
    startDate: number;
    endDate: number;
    memo: string;
    productList: any[];
    subscriber: any;
    beautyQuestionnaire: boolean;
    time: string[];
    inquiredReservationDate: number;
    requestMemo?: string;
    inquiredAttachments?: [
      {
        id: number;
        path: string;
        size: string;
      },
    ];
    attachments: [
      {
        path: string;
        size: string;
      },
    ];
  }>();
  const [questionnaireId, setQuestionnaireId] = useState();
  const [group, setGroup] = useState<{
    inquiredReservation: boolean;
    notice: string;
    address: {
      address: string;
      id: number;
      latitude: number;
      longitude: number;
    };
    usedkakoApi: boolean | undefined;
  }>();
  const [notice, setNotice] = useState<boolean>();
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const content = await apiV1.get(`/ext?key=${key}`);
      setContent(content);
      initProperties({
        groupId: content.group.id,
        petId: content.pet.id,
      });
      setIdentifyObj("petId", content.pet.id);
      setIdentifyObj("groupId", content.group.id);
      trackEvent(amplitudeEvents.reservationLand, {
        reservationNumber: key,
        status: getStatusString(content?.status),
      });
      const group = await apiV1.get(`/rest/group/${content.group.id}`);
      setGroup(group);
      const notice = await apiV1.get(`/ext/reservation/notice?key=${key}`);
      setNotice(notice);
      const warningMessage = await apiV1.get(
        `/rest/group/${content.group.id}/warning-message`,
      );
      setWarningMessage(warningMessage.message);
      const questionnaires = await apiV1.get(
        `/rest/questionnaires?groupId=${content.group.id}`,
      );
      setQuestionnaireId(
        questionnaires.filter((q: any) => q.groupEnabled)?.[0]?.id,
      );
      const history = await apiV1.get(
        `/ext/questionnaire/history?groupId=${group.id}&petId=${content.pet.id}&userId=${content.subscriber.id}`,
      );
      setHistory(history);
    })();
  }, [key]);

  const statusStrings = [
    // 순서 변경 하지 말 것 (db순서와 동일 해야함)
    [
      "문의 진행중",
      `${content?.group?.title}에서 문의를 확인하고 있어요`,
      "icon_ongoing",
      1,
      "문의등록 완료",
      "업체에서 문의를 확인하고 있어요.",
    ],
    ["문의 종료", "문의가 종료되었습니다", "icon_cancel", 1],
    ["예약 진행중", "", "icon_check", 1],
    [
      "예약 확정",
      "예약이 확정되었어요",
      "icon_check",
      1,
      "예약확정 완료",
      "정보를 확인하고 방문여부를 알려주세요.",
    ],
    [
      "이용 완료",
      "이용이 완료되었어요",
      "icon_check",
      2,
      "서비스 이용완료",
      "이용해주셔서 감사합니다.",
    ],
    ["예약 취소", "예약이 취소되었습니다", "icon_cancel", 1],
    ["예약 취소", "예약이 취소되었습니다", "icon_cancel", 1],
    ["미방문", "미방문 처리되었습니다", "icon_cancel", 1],
    ["삭제", "", "icon_cancel", 1],
    ["삭제", "", "icon_cancel", 1],
    [
      "방문 확정",
      `${content?.group?.title} 방문이 확정되었어요`,
      "icon_check",
      1,
      "방문확정 완료",
      "예약일에 만나요.",
    ],
  ];
  const getStatusString = (status: number) => {
    return statusStrings[status][0] as string;
  };
  const getStatusDescriptionString = (status: number) => {
    return statusStrings[status][1] as string;
  };
  const getStatusIcon = (status: number) => {
    return statusStrings[status][2] as string;
  };
  const getNoticeType = (status: number) => {
    return statusStrings[status][3] as number;
  };
  const getStatusDes1 = (status: number) => {
    return statusStrings[status][4] as string;
  };
  const getStatusDes2 = (status: number) => {
    return statusStrings[status][5] as string;
  };

  const handleConfirmVisit = async () => {
    const result = await apiV1.put(
      `/rest/reservation/${key}/status?status=${RESERVATION_STATUS.CONFIRM_VISIT}`,
    );
    setVisitConfirmBottomSheetOpened(false);

    if (result) {
      toast.success("방문을 확정하였습니다");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  if (_.isEmpty(content)) {
    return <></>;
  }

  return (
    <>
      {stageVisible ? (
        <VFlex
          abs
          bc-ww
          p-24-t
          p-24-rl
          style={{
            maxWidth: 480,
            minHeight: 900,
            width: "100%",
            zIndex: 1000001,
          }}
        >
          <VFlex t-24-600 p-16-t>
            <VFlex l-n>{getStatusDes1(content?.status)}</VFlex>
            <VFlex l-n>{getStatusDes2(content?.status)}</VFlex>
          </VFlex>
          <VFlex
            a-c
            style={{
              padding: "110px 0",
            }}
          >
            {getStatusString(content?.status) === "이용 완료" ? (
              <Image size={300} src={`/image/reservationstage_done.png`} />
            ) : (
              <Image size={300} src={`/image/reservationstage.png`} />
            )}
            {questionnaireId &&
              (getStatusString(content?.status) === "문의 진행중" ||
                getStatusString(content?.status) === "예약 확정" ||
                getStatusString(content?.status) === "방문 확정") && (
                <HFlex
                  m-30-t
                  a-c
                  onClick={() => {
                    if (content.beautyQuestionnaire) {
                      navigate("/questionnaireresult", {
                        state: {
                          sourceQuestionnaire: history
                            .slice(0, 1)
                            .map((item) => {
                              return item;
                            }),
                        },
                      });
                    } else {
                      navigate(`/questionnaire`, {
                        state: {
                          group: content?.group,
                          pet: content?.pet,
                          subscriber: content?.subscriber,
                          key,
                          questionnaireId,
                        },
                      });
                    }
                  }}
                >
                  <VFlex t-18-600-s4>
                    {content.beautyQuestionnaire
                      ? "작성한 안전 문진표 확인하기"
                      : "우리 아이 건강 상태 점검하기"}
                  </VFlex>
                  <SvgIcon size={24} icon={SvgIconType.ArrowRight} />
                </HFlex>
              )}

            {questionnaireId &&
              getStatusString(content?.status) === "이용 완료" &&
              content.beautyQuestionnaire && (
                <HFlex
                  m-30-t
                  a-c
                  onClick={() => {
                    navigate("/questionnaireresult", {
                      state: {
                        sourceQuestionnaire: history.slice(0, 1).map((item) => {
                          return item;
                        }),
                      },
                    });
                  }}
                >
                  <VFlex t-18-600-s4>작성한 문진표 확인하기</VFlex>
                  <SvgIcon size={24} icon={SvgIconType.ArrowRight} />
                </HFlex>
              )}
          </VFlex>

          <VFlex a-c g-16>
            <Image width={328} src={`/image/stage_${content?.status}.png`} />
            <HFlex t-14-s6>
              <Text style={{ marginRight: 33 }}>문의 등록</Text>
              <Text style={{ marginRight: 33 }}>확인중</Text>
              <Text style={{ marginRight: 27 }}>예약확정</Text>
              <Text style={{ marginRight: 27 }}>방문확정</Text>
              <Text>이용완료</Text>
            </HFlex>
          </VFlex>

          <VFlex
            a-st
            p-24
            height={112}
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000000,
            }}
          >
            <VFlex
              f-1
              c-c
              bdr-16
              t-18-ww
              bc-pt
              onClick={() => {
                setStageVisible(false);
                setTimeout(() => {
                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }, 0);
              }}
            >
              확인
            </VFlex>
          </VFlex>
          {/* E stage */}
        </VFlex>
      ) : (
        <VFlex f-1 as-st c-c style={{ backgroundColor: "#F2F5F4" }}>
          <VFlex
            style={{
              maxWidth: 480,
              width: "100%",
              paddingBottom:
                getStatusString(content?.status) === "예약 확정" ||
                getStatusString(content?.status) === "이용 완료" ||
                getStatusString(content?.status) === "미방문" ||
                getStatusString(content?.status) === "예약 취소"
                  ? "100px"
                  : 0,
            }}
          >
            <VFlex g-16 p-24>
              <HFlex a-c j-b height={48}>
                <SvgIcon width={128} height={28} icon={SvgIconType.Logo} />

                {/* <HFlex
                  p-8-tb
                  p-16-rl
                  bd-t4
                  bdr-24
                  a-c
                  onClick={() => navigate("/coparent")}
                >
                  <Text t-14-500-s6>가족과 함께 관리</Text>
                </HFlex> */}
              </HFlex>

              <TopBanner location={TopBannerLocation.reservation} />

              <VFlex g-16>
                {questionnaireId &&
                  (getStatusString(content?.status) === "문의 진행중" ||
                    getStatusString(content?.status) === "예약 확정" ||
                    getStatusString(content?.status) === "방문 확정") &&
                  !content.beautyQuestionnaire && (
                    <VFlex
                      p-24
                      bdr-24
                      bc-ww
                      g-16
                      onClick={() => {
                        navigate(`/questionnaire`, {
                          state: {
                            group: content.group,
                            pet: content.pet,
                            subscriber: content.subscriber,
                            key,
                            questionnaireId,
                          },
                        });
                      }}
                    >
                      <HFlex a-c j-b>
                        <HFlex g-16 a-c>
                          <SvgIcon width={36} icon={SvgIconType.SafetyNote} />
                          <VFlex t-22-600>안전 문진표 작성하기</VFlex>
                        </HFlex>
                        <VFlex>
                          <SvgIcon
                            width={24}
                            icon={SvgIconType.ArrowRightBig}
                          />
                        </VFlex>
                      </HFlex>
                    </VFlex>
                  )}

                {questionnaireId &&
                  notice &&
                  getStatusString(content?.status) === "이용 완료" && (
                    <VFlex
                      p-24
                      bdr-24
                      bc-ww
                      g-16
                      onClick={() => {
                        navigate(`/servicenote?revNo=${key}`, {
                          state: true,
                        });
                      }}
                    >
                      <HFlex a-c j-b>
                        <HFlex g-16 a-c>
                          <SvgIcon width={36} icon={SvgIconType.ServiceNote} />
                          <VFlex t-22-600>새로운 알림장 도착</VFlex>
                        </HFlex>
                        <VFlex>
                          <SvgIcon
                            width={24}
                            icon={SvgIconType.ArrowRightBig}
                          />
                        </VFlex>
                      </HFlex>
                    </VFlex>
                  )}

                <VFlex p-24 bdr-24 bc-ww g-16>
                  <HFlex a-c j-b>
                    <HFlex g-16 a-c>
                      <Image
                        d-24
                        src={`/image/${getStatusIcon(content?.status)}.png`}
                      />
                      <Text
                        t-22-600
                        style={{
                          color:
                            getStatusString(content?.status) === "미방문" ||
                            getStatusString(content?.status) === "예약 취소"
                              ? "#F26312"
                              : "#27BD63",
                        }}
                      >
                        {getStatusString(content?.status)}
                      </Text>
                    </HFlex>
                    {(getStatusString(content?.status) === "문의 진행중" ||
                      getStatusString(content?.status) === "예약 확정" ||
                      getStatusString(content?.status) === "방문 확정" ||
                      getStatusString(content?.status) === "이용 완료") && (
                      <VFlex
                        p-12-rl
                        p-6-tb
                        bc-t2
                        bdr-12
                        t-14-s6
                        onClick={() => {
                          setStageVisible(true);
                        }}
                      >
                        진행현황
                      </VFlex>
                    )}
                  </HFlex>
                  {getStatusDescriptionString(content?.status) && (
                    <Text t-16-s4 l-n>
                      {getStatusDescriptionString(content?.status)}
                    </Text>
                  )}
                  {/* TODO : 결제 추가 후 작업 예정
              <HFlex g-16>
                <VFlex>
                  <SvgIcon width={48} icon={SvgIconType.PaymentComplete} />
                </VFlex>
                <VFlex>
                  <VFlex t-14-s4>예약금</VFlex>
                  <VFlex t-18-600-s6>20,000원</VFlex>
                  <VFlex t-14-pt>결제가 완료되었습니다</VFlex>
                </VFlex>
              </HFlex>  */}
                  <>
                    {questionnaireId &&
                      (getStatusString(content?.status) === "문의 진행중" ||
                        getStatusString(content?.status) === "예약 확정" ||
                        getStatusString(content?.status) === "방문 확정" ||
                        getStatusString(content?.status) === "이용 완료") &&
                      content.beautyQuestionnaire && (
                        <VFlex
                          height={56}
                          bdr-16
                          bc-gl
                          a-c
                          j-c
                          t-16-s7
                          onClick={() => {
                            navigate("/questionnaireresult", {
                              state: {
                                sourceQuestionnaire: history
                                  .slice(0, 1)
                                  .map((item) => {
                                    return item;
                                  }),
                              },
                            });
                          }}
                        >
                          내가 작성한 문진표 보기
                        </VFlex>
                      )}
                  </>
                </VFlex>

                {getStatusString(content?.status) === "미방문" ||
                getStatusString(content?.status) === "예약 취소" ? (
                  <></>
                ) : (
                  <VFlex p-24 bdr-24 bc-ww g-24>
                    <HFlex g-16 a-c>
                      <SvgIcon icon={SvgIconType.Speaker} />
                      <Text t-22-600-s8>
                        {getNoticeType(content?.status) === 1
                          ? "예약 시 안내사항"
                          : "서비스 후 주의사항"}
                      </Text>
                    </HFlex>
                    <VFlex t-16-s4>
                      {getNoticeType(content?.status) === 1 ? (
                        <Text l-n>{group?.notice}</Text>
                      ) : (
                        <Text l-n>{warningMessage}</Text>
                      )}
                    </VFlex>
                  </VFlex>
                )}

                <VFlex p-24 bdr-24 bc-ww g-24>
                  <VFlex g-8>
                    <Text t-22-600-s8>예약 정보</Text>
                    <Text t-14-500-s4>{key}</Text>
                  </VFlex>

                  <HFlex g-16>
                    <Image
                      d-48
                      bdr-48
                      src={
                        content.pet?.profile_image?.path ??
                        "/image/profile_example.png"
                      }
                    />
                    <VFlex>
                      <Text t-14-500-g6>반려동물 정보</Text>
                      <Text t-18-600-s6 l-n>
                        {content?.pet?.name} &#40;{content?.pet?.species.name}
                        &#41;
                      </Text>
                    </VFlex>
                  </HFlex>
                  <Divider />
                  <HFlex g-16>
                    <HFlex c-c d-48 bdr-48 bc-g9 sized>
                      <SvgIcon icon={SvgIconType.Store} />
                    </HFlex>
                    <VFlex>
                      <Text t-14-g6>업체명</Text>
                      <Text t-18-600-s6 l-n>
                        {content?.group?.title}
                      </Text>
                    </VFlex>
                  </HFlex>
                  {content?.startDate ? (
                    <>
                      <HFlex g-16>
                        <HFlex c-c d-48 bdr-48 bc-g9 sized>
                          <SvgIcon icon={SvgIconType.Time} />
                        </HFlex>
                        <VFlex>
                          <Text t-14-g6>
                            {format(
                              new Date(content?.startDate * 1000),
                              "yyyy년 M월 d일 (EEE)",
                              {
                                locale: ko,
                              },
                            )}
                          </Text>
                          <Text t-18-600-s6>
                            {format(
                              new Date(content?.startDate * 1000),
                              "a h:mm",
                              {
                                locale: ko,
                              },
                            )}
                          </Text>
                        </VFlex>
                      </HFlex>
                    </>
                  ) : (
                    <>
                      <HFlex g-16>
                        <HFlex c-c d-48 bdr-48 bc-g9 sized>
                          <SvgIcon icon={SvgIconType.Date} />
                        </HFlex>
                        <VFlex>
                          <Text t-14-g6>희망 날짜</Text>
                          <Text t-18-600-s6>
                            {format(
                              new Date(content?.inquiredReservationDate * 1000),
                              "M월 d일 EEEE",
                              {
                                locale: ko,
                              },
                            )}
                          </Text>
                        </VFlex>
                      </HFlex>
                      <HFlex g-16>
                        <HFlex c-c d-48 bdr-48 bc-g9 sized>
                          <SvgIcon icon={SvgIconType.Time} />
                        </HFlex>
                        <VFlex>
                          <Text t-14-g6>희망 시작 시간</Text>
                          <Text t-18-600-s6 l-n>
                            {content?.time?.map((value) => value).join(", ")}
                          </Text>
                        </VFlex>
                      </HFlex>
                    </>
                  )}
                  <HFlex g-16>
                    <HFlex c-c d-48 bdr-48 bc-g9 sized>
                      <SvgIcon icon={SvgIconType.Service} />
                    </HFlex>
                    <VFlex>
                      <Text t-14-g6>서비스명</Text>
                      <Text t-18-600-s6 l-n>
                        {content?.productList
                          ?.map((product) => product.name)
                          .join(", ")}
                      </Text>
                    </VFlex>
                  </HFlex>
                  <HFlex g-16>
                    <HFlex c-c d-48 bdr-48 bc-g9 sized>
                      <SvgIcon icon={SvgIconType.MemoColor} />
                    </HFlex>
                    <VFlex>
                      <VFlex>
                        <Text t-14-g6>요청사항</Text>
                        <Text
                          t-18-600-s6
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {content?.memo || content.requestMemo || "-"}
                        </Text>
                      </VFlex>
                      <VFlex>
                        {(content.attachments ||
                          content.inquiredAttachments) && (
                          <HFlex g-8>
                            {(
                              content.attachments || content.inquiredAttachments
                            ).map((att, i) => {
                              return (
                                <VFlex
                                  key={i}
                                  p-12-t
                                  rel
                                  style={{
                                    display: i < 4 ? "block" : "none",
                                  }}
                                  onClick={() => {
                                    navigate("/reservationnotephotos", {
                                      state: { content },
                                    });
                                  }}
                                >
                                  <Image
                                    width={56}
                                    height={56}
                                    m-8-t
                                    bdr-16
                                    src={att.path}
                                  />
                                  {(
                                    content.attachments ||
                                    content.inquiredAttachments
                                  ).length > 4 &&
                                    i === 3 && (
                                      <VFlex
                                        abs
                                        a-c
                                        j-c
                                        width={"100%"}
                                        height={56}
                                        t-20-600-ww
                                        bdr-16
                                        style={{
                                          top: 20,
                                          left: 0,
                                          background: "rgba(0,0,0,0.3)",
                                        }}
                                      >
                                        +
                                        {(
                                          content.attachments ||
                                          content.inquiredAttachments
                                        ).length - 4}
                                      </VFlex>
                                    )}
                                </VFlex>
                              );
                            })}
                          </HFlex>
                        )}
                      </VFlex>
                    </VFlex>
                  </HFlex>
                  {!isWebView && (
                    <HFlex
                      bd-h2
                      p-16-tb
                      bdr-16
                      c-c
                      onClick={() => {
                        trackEvent(amplitudeEvents.appDownloadBtnClick, {
                          location: "service_note_history_btn",
                        });
                        openAppLink("button_reservation");
                      }}
                    >
                      <Text t-16-600-h2>
                        {content?.pet?.name}의 지난 예약 내역 보기
                      </Text>
                    </HFlex>
                  )}
                </VFlex>

                <VFlex p-24 bdr-24 bc-ww g-24>
                  <Text t-22-600-s8>업체 정보</Text>
                  <VFlex g-16>
                    <KakaoMapContainer
                      company={content?.group?.title || ""}
                      address1={content?.group?.address?.address || ""}
                      address2={""}
                      addressPost={""}
                      tel={content?.group?.contact || ""}
                      designatedCoords={
                        group && group.usedkakoApi === false
                          ? {
                              latitude: group.address.latitude,
                              longitude: group.address.longitude,
                            }
                          : undefined
                      }
                    />
                  </VFlex>
                </VFlex>
                <VFlex p-24 bdr-24 bc-ww g-24>
                  <Text t-22-600-s8>예약자 정보</Text>
                  <VFlex g-16>
                    <VFlex>
                      <Text t-18-600-g3>
                        {content?.subscriber?.nickname
                          ? content?.subscriber?.nickname
                          : content?.subscriber?.name}
                      </Text>
                      <Text t-14-g6>{content?.subscriber?.cellnumber}</Text>
                    </VFlex>
                  </VFlex>
                </VFlex>
              </VFlex>
            </VFlex>
            <VFlex p-24 m-24-t m-32-b g-16>
              <SvgIcon width={75} height={16} icon={SvgIconType.Logo} />
              <Text t-12-g5 style={{ whiteSpace: "break-spaces" }}>
                주식회사 포들러스 | 서비스 이용약관 | 대표자 : 이신혜 |
                02-6953-5788 사업자등록번호: 696-86-02652 | 통신판매업 : 제
                2022-서울성동-01686 호 주소 : 서울특별시 성동구 왕십리로 130,
                10층 (성수동1, KCC 프리미어 타워)
              </Text>
            </VFlex>
          </VFlex>
          {!isWebView &&
            (getStatusString(content?.status) === "이용 완료" ||
              getStatusString(content?.status) === "미방문" ||
              getStatusString(content?.status) === "예약 취소") &&
            group?.inquiredReservation !== false && (
              <VFlex
                a-st
                p-24
                height={112}
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1000000,
                }}
              >
                <VFlex
                  f-1
                  c-c
                  bdr-16
                  t-18-ww
                  bc-pt
                  onClick={() => {
                    navigate(`/schedule`, {
                      state: {
                        group: content.group,
                        pet: content.pet,
                        subscriber: content.subscriber,
                        type: "USER_WEB",
                      },
                    });
                  }}
                >
                  재예약 문의하기
                </VFlex>
              </VFlex>
            )}

          {getStatusString(content?.status) === "예약 확정" && (
            <VFlex
              a-st
              p-24
              height={112}
              style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1000000,
              }}
            >
              <VFlex
                f-1
                c-c
                bdr-16
                t-18-ww
                bc-pt
                onClick={() => {
                  setVisitConfirmBottomSheetOpened(true);
                }}
              >
                방문 확정
              </VFlex>
            </VFlex>
          )}

          <VisitConfirmBottomSheet
            open={visitConfirmBottomSheetOpened}
            onDismiss={() => setVisitConfirmBottomSheetOpened(false)}
            onComplete={() => {
              handleConfirmVisit();
              trackEvent(amplitudeEvents.reservationVisitConfirm, {
                reservationNumber: key,
              });
              setVisitConfirmBottomSheetOpened(false);
            }}
          />
        </VFlex>
      )}
    </>
  );
}
